import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { hashContent } from '@/lib/creditGuard';

interface JobToAdd {
  job_id: string;
  title: string;
  company: string;
  location?: string;
  description?: string;
  url?: string;
  salary?: string;
  remote?: string;
  job_type?: string;
  logo?: string;
}

export interface SmartApplyJob {
  id: string;
  user_id: string;
  job_id: string;
  job_title: string;
  company_name: string;
  job_url: string | null;
  job_description: string | null;
  location: string | null;
  salary: string | null;
  job_type: string | null;
  remote: string | null;
  logo: string | null;
  custom_resume_id: string | null;
  custom_resume_json: any; // Resume JSON structure for preview
  custom_resume_pdf_url: string | null;
  custom_cover_letter_text: string | null;
  custom_cover_letter_id: string | null;
  application_form_data: any;
  match_score: number | null;
  ats_score: number | null;
  status: 'preparing' | 'ready' | 'applied' | 'skipped';
  created_at: string;
  ready_at: string | null;
  applied_at: string | null;
  extension_task_id: string | null; // Link to extension_tasks for auto-apply
}

export const useSmartApply = () => {
  const [loading, setLoading] = useState(false);

  const addToSmartApply = async (job: JobToAdd) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to use Smart Apply');
        return false;
      }

      // Check if job already in queue
      const { data: existing } = await supabase
        .from('smart_apply_queue')
        .select('id')
        .eq('user_id', user.id)
        .eq('job_id', job.job_id)
        .maybeSingle();

      if (existing) {
        toast.info('Job is already in your Smart Apply queue');
        return false;
      }

      // Charge credits BEFORE adding to queue (5 credits, Premium feature)
      const contextData = `smartapply:${job.job_id}:${Date.now()}`;
      const contextHash = await hashContent(contextData);
      const refId = `smartapply:${job.job_id}:${contextHash}`;

      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'smart_apply', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'PREMIUM_REQUIRED') {
          toast.error(errorData.message || 'Smart Apply requires a Premium plan', {
            action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' },
          });
          return false;
        }
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => window.location.href = '/pricing' },
          });
          return false;
        }
        throw creditError;
      }

      // Add to queue with "preparing" status
      const { data: inserted, error } = await supabase
        .from('smart_apply_queue')
        .insert({
          user_id: user.id,
          job_id: job.job_id,
          job_title: job.title,
          company_name: job.company,
          job_url: job.url,
          job_description: job.description,
          location: job.location,
          salary: job.salary,
          job_type: job.job_type,
          remote: job.remote,
          logo: job.logo,
          status: 'preparing',
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding to Smart Apply:', error);
        toast.error('Failed to add job to Smart Apply');
        return false;
      }

      // Show credit usage toast
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`${creditResult.cost} credits used for Smart Apply. ${creditResult.remaining} remaining.`);
      }

      // Trigger background preparation with auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        supabase.functions.invoke('prepare-smart-apply', {
          body: { queueId: inserted.id },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }).then(({ data, error }) => {
          if (error) {
            console.error('Error preparing smart apply:', error);
            const errorMessage = error.message || 'Failed to prepare application';
            if (errorMessage.includes('usage limit') || errorMessage.includes('credits')) {
              toast.error('AI usage limit reached. Please try again later or contact support.');
            } else {
              toast.error('Failed to prepare application materials. Please try again.');
            }
          }
        });
      }

      window.dispatchEvent(new CustomEvent('creditsUpdated'));
      toast.success('Job added to Smart Apply! AI is preparing your materials...');
      return true;
    } catch (error) {
      console.error('Error adding to Smart Apply:', error);
      toast.error('Failed to add job to Smart Apply');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (queueId: string, status: 'applied' | 'skipped') => {
    try {
      const { error } = await supabase
        .from('smart_apply_queue')
        .update({ status })
        .eq('id', queueId);

      if (error) throw error;

      toast.success(status === 'applied' ? 'Marked as applied!' : 'Job skipped');
      return true;
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
      return false;
    }
  };

  const deleteFromQueue = async (queueId: string) => {
    try {
      const { error } = await supabase
        .from('smart_apply_queue')
        .delete()
        .eq('id', queueId);

      if (error) throw error;

      toast.success('Removed from Smart Apply queue');
      return true;
    } catch (error) {
      console.error('Error deleting from queue:', error);
      toast.error('Failed to remove from queue');
      return false;
    }
  };

  return {
    addToSmartApply,
    updateStatus,
    deleteFromQueue,
    loading,
  };
};
